import { useQuery } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import type {
  DeviceCalendarSlotsRequest,
  DeviceCalendarSlotsResponse,
} from '@/types/scheduling-api';

/**
 * Hits POST /api/v1/Practice/DeviceCalendarSlots — the Devices tab's card feed
 * (PROD-2184; endpoint PROD-2181, aggregator passthrough PROD-2183).
 *
 * The device-side twin of `useCalendarSlots`, on its own endpoint on purpose
 * (ruling 2026-09-13): the practitioner route carries a photo SAS URI, a
 * licence-expiry check and four practitioner-only objects per row that a
 * machine has no use for, on the hottest calendar query there is.
 *
 * Feed the response through `devicesFromDeviceCalendarSlots` to get the
 * `ProviderSummary[]` the existing `ProviderStrip` renders.
 */

interface DeviceCalendarSlotsParams {
  organizationId: string;
  practiceId: string;
  /** ISO date or full datetime; the hook ensures the day-end behavior. */
  startDate: string;
  endDate: string;
  /** Empty / omitted: every live device at the practice. */
  devices?: string[];
  searchText?: string;
  /**
   * How many free slots to carry per device. Omitted: `CARD_SLOT_COUNT` (3) —
   * the Devices-tab strip, which shows three "Next" chips.
   *
   * The Book-a-device rail asks for the whole remaining day instead
   * (PROD-2455), because it is no longer showing a teaser: it now READS its
   * bookable starts from this feed rather than enumerating them from
   * `ResourceAvailability`. Parameterising the existing hook keeps both
   * callers on one query implementation, and the count is part of the query
   * key, so a 3-slot answer and a whole-day answer cannot overwrite each other
   * in the cache.
   */
  slotCount?: number;
  /**
   * PROD-2456. The product being booked: the backend cuts that device's slots
   * at THIS product's minutes, exactly as `Appointment/Slots` follows a
   * practitioner's `appointmentProfileId`. Omitted: the device's default
   * product, which is what the Devices-tab strip wants — so the strip sends
   * nothing and its request body is unchanged.
   *
   * Part of the query key (the whole `params` object is), for the same reason
   * `slotCount` is: a 30-minute grid and a 90-minute grid for the same device
   * and day are different answers and must never share a cache entry.
   */
  appointmentProfileId?: string;
}

/**
 * Same contract `useCalendarSlots` mirrors from Flutter's datasource: a caller
 * may pass `YYYY-MM-DD` or a full ISO, and we normalize to a full-day range so
 * the backend's `DateTime` binding sees the whole day rather than midnight.
 */
function toDayBoundaryISO(input: string, end: boolean): string {
  // Already a full datetime — pass through.
  if (input.includes('T')) return input;
  return end ? `${input}T23:59:59Z` : `${input}T00:00:00Z`;
}

/** The strip shows three "Next" chips; `totalSlots` still carries the count. */
const CARD_SLOT_COUNT = 3;

/**
 * `enabled` gates the FETCH without disturbing the query key (PROD-2364),
 * mirroring `useCalendarDevices(practiceId, enabled)`. Callers used to gate by
 * passing `null` params, which also changed the key — so the answer this feed
 * had already given was dropped the moment the Devices tab lost focus, and the
 * tab badge went back to not knowing how many devices exist.
 */
/**
 * NO CACHE — Andrew, 2026-09-21: "don't cache the calendar slots at all"
 * (PROD-2444).
 *
 * The app-wide default is `staleTime: 5 * 60_000` with `refetchOnWindowFocus`
 * and `refetchOnReconnect` both OFF, which is right for a service catalogue
 * and wrong for this: a slot feed answers *what is free right now*, and two
 * operators booking the same machine minutes apart is the case a five-minute
 * memory loses. `refetchOnMount: 'always'` alone only covers a fresh mount.
 *
 *   staleTime 0            — never reuse an answer without asking again
 *   gcTime 0               — drop it the moment nothing is watching, so a
 *                            remount shows a loading state rather than
 *                            painting yesterday's windows and correcting
 *                            itself a beat later
 *   refetchOnMount 'always'
 *   refetchOnWindowFocus / refetchOnReconnect — re-read after the operator
 *                            comes back from another tab or a dropped link,
 *                            overriding the global opt-outs on purpose
 *
 * `gcTime: 0` is safe for the consumers that gate with `enabled` rather than
 * by unmounting (the Devices tab badge, PROD-2364): a disabled query still has
 * a mounted observer, so nothing is collected while the page is on screen.
 */
const NO_CACHE = {
  staleTime: 0,
  gcTime: 0,
  refetchOnMount: 'always',
  refetchOnWindowFocus: true,
  refetchOnReconnect: true,
} as const;

export function useDeviceCalendarSlots(
  params: DeviceCalendarSlotsParams | null,
  enabled = true,
) {
  return useQuery({
    queryKey: ['device-calendar-slots', params],
    queryFn: async ({ signal }) => {
      if (!params) throw new Error('useDeviceCalendarSlots called without params');
      const body: DeviceCalendarSlotsRequest = {
        organizationId: params.organizationId,
        practiceId: params.practiceId,
        startDate: toDayBoundaryISO(params.startDate, false),
        endDate: toDayBoundaryISO(params.endDate, true),
        devices: params.devices ?? [],
        searchText: params.searchText ?? '',
        slotCount: params.slotCount ?? CARD_SLOT_COUNT,
        // Only when there is one: an absent field is the default-product grid.
        ...(params.appointmentProfileId
          ? { appointmentProfileId: params.appointmentProfileId }
          : {}),
      };
      const res = await post<DeviceCalendarSlotsResponse>(
        '/api/v1/Practice/DeviceCalendarSlots',
        body,
        { signal },
      );
      // A practice with no devices returns an empty/null body. React Query
      // rejects an `undefined` return ("data cannot be undefined"), which
      // would fire the global error toast — coalesce to [] exactly as
      // `useCalendarSlots` does.
      return res ?? [];
    },
    enabled: enabled && !!params?.practiceId,
    ...NO_CACHE,
  });
}
